import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import DataTable from '../../components/DataTable';
import UserDetails from '../../components/popup/UserDetails';
import { ResumeButton } from '../../components/ActionButtons';

// Constants
import {
  ADMIN_CANDIDATE_FILTER_KEY,
  CANDIDATE_SOURCE_TYPE,
  CANDIDATE_SOURCE_REFERENCE_CODE,
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  TABLE_NAME
} from '../../constants/common';
import {
  ADMIN_CONFIRM_CANDIDATE_COLUMNS,
  ADMIN_NOT_CONFIRM_CANDIDATE_COLUMNS
} from '../../constants/tableColumns';

// Redux-Actions
import { getCandidateList } from '../../redux/actions/candidates';
import { candidateDetailsAction } from '../../redux/actions/userDetails';

// Utils
import {
  downloadPdf,
  formatExperience,
  isDarkMode,
  normalizeObject,
  openResumeLink,
  renderHrAvatar,
  wordCapitalize
} from '../../utils/common';
import { debounce } from '../../utils/debounce';

// Apis
import { TabPanel, TabView } from 'primereact/tabview';
import { getInterviewTechnology } from '../../redux/actions/hr';
import { filteredHrListApi } from '../../redux/apis/hr';
import CopyToClipboard from '../../components/CopyToClipboard';

const pathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/resume`;
const profilePathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/profile`;

const adminCandidateTableStates = {
  searchValue: '',
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  sortBy: 'DateModified',
  orderBy: 'desc',
  minExp: null,
  maxExp: null,
  technology: '',
  filterByHR: [],
  isConfirmedUser: true
};

const AdminCandidatesList = () => {
  const dispatch = useDispatch();
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const { candidates, userDetails } = useSelector((state) => state);
  const [candidate, setCandidate] = useState([]);
  const [candidateExport, setCandidateExport] = useState([]);
  const [hrs, setHrs] = useState([]);
  // const [tableStates, setTableStates] = useState(adminCandidateTableStates);
  const [candidateId, setCandidateId] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [isHoveredImage, setIsHoveredImage] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeIndex, setActiveIndex] = useState(0);
  const normalizeTableState = (state) => {
    if (!state) return adminCandidateTableStates;

    return {
      ...adminCandidateTableStates,
      ...state,
      technology: Array.isArray(state.technology)
        ? state.technology
        : state.technology
        ? [state.technology]
        : []
    };
  };
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(ADMIN_CANDIDATE_FILTER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  const [tableStates, setTableStates] = useState(() => {
    const saved = getSavedFilters();
    return saved ? normalizeTableState(saved) : adminCandidateTableStates;
  });
  useEffect(() => {
    const { searchValue, minExp, maxExp, technology, filterByHR, isConfirmedUser } = tableStates;

    const isFilterApplied =
      searchValue?.trim() !== '' ||
      minExp !== null ||
      maxExp !== null ||
      (Array.isArray(technology) ? technology.length > 0 : technology?.trim() !== '') ||
      filterByHR?.length > 0 ||
      isConfirmedUser !== true;

    if (isFilterApplied) {
      localStorage.setItem(ADMIN_CANDIDATE_FILTER_KEY, JSON.stringify(tableStates));
    } else {
      localStorage.removeItem(ADMIN_CANDIDATE_FILTER_KEY);
    }
  }, [tableStates]);

  const handleMouseMove = (event) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const onTogglePopup = () => setShowPopup((prevState) => !prevState);

  // Experience Year Filter
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    dispatch(getCandidateList(tableStates));
  }, [dispatch, tableStates, candidates?.isDeleted, candidates?.isUpdated, candidates?.isAdded]);

  useEffect(() => {
    const modifiedCandidate = candidates?.data?.map((candidateList, index) => {
      return {
        ...candidateList,
        HRName: renderHrAvatar(
          candidateList?.ReportsToUser?.ID,
          candidateList?.ReportsToUser?.UserProfile,
          candidateList?.ReportsToUser?.FirstName,
          candidateList?.ReportsToUser?.LastName
        ),
        CandidateName: (
          <div className="flex" style={{ position: 'relative' }}>
            <span
              className="table-view-popup-link"
              onClick={() => handleView(candidateList.ID)}
              title={`Mobile : ${candidateList.Mobile || '--'}`}>
              {`${wordCapitalize(candidateList.FirstName)} ${wordCapitalize(
                candidateList.LastName
              )}`}
            </span>
            {candidateList?.UserProfile && candidateList?.ID && (
              <div
                onMouseEnter={() => {
                  if (candidateList?.UserProfile && candidateList?.ID) {
                    let hoveredImage = { ...isHoveredImage, [candidateList?.ID]: true };
                    setIsHoveredImage(hoveredImage);
                  }
                }}
                onMouseLeave={() => {
                  let hoveredImage = { ...isHoveredImage, [candidateList?.ID]: false };
                  setIsHoveredImage(hoveredImage);
                }}
                onMouseMove={handleMouseMove}>
                <i
                  onClick={() =>
                    openResumeLink(
                      `${profilePathBase}/${candidateList?.ID}/${candidateList?.UserProfile}`
                    )
                  }
                  className="pi pi-fw pi-eye"
                />
                {isHoveredImage[candidateList?.ID] && (
                  <div
                    className="image-preview"
                    key={index}
                    style={{
                      top: `${mousePosition.y + 10}px`,
                      left: `${mousePosition.x + 10}px`
                    }}>
                    <img
                      src={`${profilePathBase}/${candidateList?.ID}/${candidateList?.UserProfile}`}
                      alt="Preview"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ),
        Technology: (
          <ul className="technology-list" key={index}>
            {candidateList.UMap && candidateList.UMap.length > 0
              ? candidateList.UMap?.map((tech) => (
                  <li
                    key={tech.MapT.ID}
                    style={{
                      backgroundColor: `${
                        isDarkMode() ? tech?.MapT?.DarkModeColor : tech?.MapT?.TechnologyColor
                      }`
                    }}
                    className="technology-badge">
                    {tech.MapT.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),
        CurrentCompany: candidateList?.CurrentCompany || '',
        Experience: formatExperience(
          candidateList.YearsOfExperience,
          candidateList?.MonthsOfExperience
        ),
        Mobile: (
          <div className="center">
            <span>{candidateList.Mobile || '--'}</span>
            {candidateList.Mobile && <CopyToClipboard text={candidateList.Mobile} />}
          </div>
        ),
        Resume: (
          <div className="flex">
            {candidateList?.ResumeOfCandidate && (
              <>
                <ResumeButton
                  onClick={() => {
                    openResumeLink(
                      `${pathBase}/${candidateList?.ID}/${candidateList?.ResumeOfCandidate?.FilePath}`
                    );
                  }}
                />
                <i
                  data-tip="Download"
                  onClick={() =>
                    downloadPdf(
                      pathBase,
                      candidateList?.ID,
                      candidateList?.ResumeOfCandidate?.FilePath
                    )
                  }
                  className="pi pi-fw pi-download"
                />
              </>
            )}
          </div>
        ),
        Location: candidateList?.Address || '--'
      };
    });
    setCandidate(modifiedCandidate);
  }, [candidates, isHoveredImage, isDarkMode()]);

  useEffect(() => {
    const modifiedCandidate = candidates?.data?.map((candidateList) => {
      const name = `${wordCapitalize(candidateList.FirstName)} ${wordCapitalize(
        candidateList.LastName
      )}`;
      const hr = candidateList?.ReportsToUser || '';
      return {
        CandidateName: name,
        HRName: hr?.FirstName + ' ' + hr?.LastName,
        Technology:
          candidateList?.UMap && candidateList.UMap.length > 0
            ? candidateList.UMap.map((obj) => obj.MapT.Technology).join(', ')
            : '',
        Experience: formatExperience(
          candidateList.YearsOfExperience,
          candidateList?.MonthsOfExperience
        ),
        Mobile: candidateList.Mobile || '--'
      };
    });
    setCandidateExport(modifiedCandidate);
  }, [candidates]);

  useEffect(() => {
    candidateId && dispatch(candidateDetailsAction(candidateId));
  }, [dispatch, candidateId, userDetails?.isCandidateUpdated]);

  // Call an API on component mount only to get the list of technologies.
  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    filteredHrListApi().then((res) => {
      if (res?.data?.data?.length) {
        const sortedHrs = [...res.data.data].sort((a, b) => {
          if (a.isActive === b.isActive) return 0;
          return a.isActive ? -1 : 1;
        });

        const hrs = sortedHrs.map((hr) => ({
          name: `${hr?.FirstName || ''} ${hr?.LastName || ''}${hr?.isActive ? '' : ' (Inactive)'}`,
          id: hr.ID,
          isActive: hr?.isActive
        }));
        setHrs(hrs);
      }
    });
  }, []);

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      const candidateDetailsData = [
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName) || ''}
              ${wordCapitalize(userDetails?.LastName) || ''}`
        },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.Mobile || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={userDetails?.Mobile} />}
            </div>
          )
        },
        {
          title: 'Second Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.SecondaryMobile || '--'}</span>
              {userDetails?.SecondaryMobile && (
                <CopyToClipboard text={userDetails?.SecondaryMobile} />
              )}
            </div>
          )
        },
        {
          title: 'Current Company',
          value: userDetails?.CurrentCompany || ''
        },
        {
          title: 'Technology',
          value:
            userDetails.CandidateTechnology && userDetails.CandidateTechnology.length > 0
              ? userDetails.CandidateTechnology?.map((tech) => tech.Technology).join(', ')
              : ''
        },
        {
          title: 'Current CTC',
          value: userDetails?.CurrentCTC ? `${userDetails?.CurrentCTC} LPA` : ''
        },
        {
          title: 'Candidate Location',
          value: userDetails?.Address || ''
        },
        {
          title: 'Candidate Source',
          value: CANDIDATE_SOURCE_TYPE.find((type) => type.code === userDetails?.CandidateSource)
            ?.name
        }
      ];
      // Only add the candidate reference name if candidate provided source is reference.
      if (userDetails?.CandidateSource === CANDIDATE_SOURCE_REFERENCE_CODE) {
        candidateDetailsData.push({
          title: 'Employee Ref Name',
          value: userDetails?.EmployeeReferenceName || ''
        });
      }
      candidateDetailsData.push(
        {
          title: 'Created By',
          value: renderHrAvatar(
            userDetails?.ReportsToUser?.ID,
            userDetails?.ReportsToUser?.UserProfile,
            userDetails?.ReportsToUser?.FirstName,
            userDetails?.ReportsToUser?.LastName,
            true
          )
        },
        {
          title: 'Last Updated Time',
          value: userDetails?.DateModified ? moment(userDetails?.DateModified).format('lll') : null
        }
      );
      setCandidateDetails(candidateDetailsData);
    }
  }, [userDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = (searchValue) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        searchValue
      };
    });
  };

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleFilterByHr = (filterByHR) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        filterByHR
      };
    });
  };

  const handleYearExperienceFilter = (minExp, maxExp) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        minExp,
        maxExp
      };
    });
    handleClose();
  };

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setCandidateId(ID);
    }
  };

  const handleStatusFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        technology: value || ''
      };
    });
  };
  const handleTabView = (value) => {
    setActiveIndex(value);
    setTableStates(() => {
      return {
        ...adminCandidateTableStates,
        isConfirmedUser: !value
      };
    });
  };

  useEffect(() => {
    const totalPages = Math.ceil(candidates.pagination.totalRecords / tableStates.size);

    // If current page is no longer valid → reset to page 1
    if (tableStates.pageNumber > totalPages && totalPages > 0) {
      setTableStates((prev) => ({
        ...prev,
        pageNumber: 1
      }));
    }
  }, [candidates.pagination.totalRecords]);
  return (
    <div className="card interview-card">
      <UserDetails
        header="Candidate Details"
        data={candidateDetails}
        onHide={onTogglePopup}
        show={showPopup}
        callStatusHistory={userDetails?.CallStatusHistory}
        candidateId={candidateId}
        readOnly={true}
        onStatusUpdate={() => {
          if (candidateId) {
            dispatch(candidateDetailsAction(candidateId));
            dispatch(getCandidateList(tableStates));
          }
        }}
      />
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => handleTabView(e.index)}
        className="schedule-tabbing-wrap custom-tabbing">
        <TabPanel header="Confirm">
          <DataTable
            name={TABLE_NAME.CANDIDATE}
            searchPlaceHolder="Search by Candidate name, Email and Mobile"
            totalRecords={candidates.pagination.totalRecords}
            searchValue={tableStates.searchValue}
            pageNumber={tableStates.pageNumber}
            pageSize={tableStates.size}
            onPagination={handlePagination}
            onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
            onSort={handleSort}
            data={candidate}
            hrFilter
            hrs={hrs}
            onFilter={handleFilterByHr}
            selectedHrs={tableStates.filterByHR}
            exportData={candidateExport}
            columns={ADMIN_CONFIRM_CANDIDATE_COLUMNS}
            experienceFilter
            open={open}
            label={
              tableStates.minExp && tableStates.maxExp
                ? `${tableStates.minExp} - ${tableStates.maxExp} Years`
                : 'Years of Exp'
            }
            onExperienceFilter={handleClick}
            minExp={tableStates.minExp}
            maxExp={tableStates.maxExp}
            handleYearExperienceFilter={handleYearExperienceFilter}
            handleClose={handleClose}
            anchorEl={anchorEl}
            isDropdownMultiSelect
            dropdownPlaceHolder="Select Technology"
            onStatusFilter={handleStatusFilter}
            dropdown={
              technologyListData?.length
                ? technologyListData.map((tech) => ({ name: tech.Technology, value: tech.ID }))
                : []
            }
            dropdownValue={tableStates.technology}
            clearFilter
            isClearFilterDisabled={
              JSON.stringify(normalizeObject(adminCandidateTableStates)) ===
              JSON.stringify(normalizeObject(tableStates))
            }
            clearFilterOnClick={() => {
              setTableStates({
                ...adminCandidateTableStates,
                technology: [],
                minExp: null,
                maxExp: null,
                pageNumber: INITIAL_PAGE_NUMBER,
                size: INITIAL_SIZE
              });
              localStorage.removeItem(ADMIN_CANDIDATE_FILTER_KEY);
            }}
          />
        </TabPanel>
        <TabPanel header="Future Prospect">
          <DataTable
            name={TABLE_NAME.CANDIDATE}
            searchPlaceHolder="Search by Candidate name, Email and Mobile"
            totalRecords={candidates.pagination.totalRecords}
            onPagination={handlePagination}
            pageNumber={tableStates.pageNumber}
            pageSize={tableStates.size}
            onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
            onSort={handleSort}
            data={candidate}
            exportData={candidateExport}
            columns={ADMIN_NOT_CONFIRM_CANDIDATE_COLUMNS}
          />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default AdminCandidatesList;
